import { useState, type FormEvent } from "react";

import { supabase } from "../lib/supabase";
import { useTurnstile } from "../lib/turnstile";

type Mode = "sign-in" | "reset";

/**
 * Employers sign in with the account they already have. There is no sign-up
 * here on purpose: a household is created in the app, and an account that can
 * reach this screen already exists.
 *
 * Errors are reported as Supabase gives them, except that a failed sign-in is
 * deliberately vague about which half was wrong — telling somebody an address
 * exists but the password does not is how you enumerate customers.
 */
export function SignIn({ problem }: { problem?: string | null }) {
  const [mode, setMode] = useState<Mode>(problem ? "reset" : "sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const { token: captchaToken, reset: resetCaptcha, widget: captcha } = useTurnstile();

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    if (mode === "reset") {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/`,
        captchaToken,
      });
      setBusy(false);
      // A token is single-use whatever the outcome, so the next attempt needs
      // a fresh one.
      resetCaptcha();
      // Whether or not the address is on an account, say the same thing: the
      // reply itself must not reveal who has one.
      if (error) setError(error.message);
      else setSent(true);
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
      options: { captchaToken },
    });
    setBusy(false);
    resetCaptcha();
    if (error) {
      setError(
        error.message.toLowerCase().includes("captcha")
          // Worth its own sentence. "Invalid login credentials" would send
          // somebody hunting for a typo in a password that was correct.
          ? "The security check did not pass. Wait a moment and try again."
          : error.message.toLowerCase().includes("invalid")
            ? "That email address and password do not match."
            : error.message,
      );
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <a className="logo" href="https://shiftygroup.com">
          shifty<span>.</span>
        </a>

        {mode === "sign-in" ? (
          <>
            <h1>Sign in</h1>
            <p className="sub">
              The employer dashboard, for the desk work that is awkward on a phone.
            </p>
          </>
        ) : (
          <>
            <h1>Reset your password</h1>
            <p className="sub">
              We will email a link. Open it on this computer to finish here.
            </p>
          </>
        )}

        {/* A link that could not be finished. Shown until they do something
            else, because arriving here from an email with no explanation is
            exactly the confusion this screen is for. */}
        {problem && !error && !sent && <div className="notice error">{problem}</div>}
        {error && <div className="notice error">{error}</div>}
        {sent && (
          <div className="notice ok">
            If that address has a Shifty account, a reset link is on its way. It expires
            shortly, so use the newest email if you ask more than once.
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {mode === "sign-in" && (
            <>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}

          {/* Invisible: it draws nothing and only interrupts a request that
              looks automated. Inside the form so it is torn down with it. */}
          {captcha}

          <button className="btn" type="submit" disabled={busy}>
            {busy ? "One moment…" : mode === "sign-in" ? "Sign in" : "Email a reset link"}
          </button>
        </form>

        <button
          className="btn quiet"
          type="button"
          onClick={() => {
            setMode(mode === "sign-in" ? "reset" : "sign-in");
            setError(null);
            setSent(false);
          }}
        >
          {mode === "sign-in" ? "I have forgotten my password" : "Back to sign in"}
        </button>

        <p className="auth-foot">
          Workers use the Shifty app on their phone, not this page. Questions go to{" "}
          <a href="mailto:info@shiftygroup.com">info@shiftygroup.com</a>.
        </p>
      </div>
    </div>
  );
}
